// Input — text / password (show-hide) / email per COMPONENTS §Input.
//
// - <Label> + <Input> + error message are composed by the page; this component
//   is the input element itself, error wiring included.
// - Password type embeds a show-hide IconButton (Eye / EyeSlash icons).
// - aria-invalid + aria-describedby wired automatically when `error` present.
// - Validation is form-state-level (RHF + Zod resolver per ADR-008), not
//   prop-level; this component just renders the visual error.
// - autoComplete defaults to 'off' (app-data fields). When 'off', the input also
//   sets data-1p-ignore + data-lpignore so password managers don't pop "fill /
//   unlock" on a non-credential field — a field named e.g. "name" otherwise trips
//   their heuristics, and autocomplete=off alone is widely ignored. Credential
//   fields pass a real autoComplete (username / current-password) → no ignore →
//   the password manager engages normally.

import { useId, useState, forwardRef, type InputHTMLAttributes } from 'react';
import { icons } from './icons.js';
import { useTranslation } from 'react-i18next';
import { useSelectOnFocus } from '../../hooks/useSelectOnFocus.js';

export type InputType = 'text' | 'password' | 'email';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  type?: InputType;
  /** Error message displayed below the input. Triggers aria-invalid. */
  error?: string;
  /** Select the contents on focus so typing replaces the value (default true;
   *  the standard single-line "click the field, retype it" behavior). Pass false
   *  for a field where caret-positioning matters more than replace. */
  selectOnFocus?: boolean;
}

// Border + hover + focus light-up + invalid come from the shared `field-frame`
// utility (DESIGN-TOKENS §Interaction states) — applied to the input (text) or
// the wrapper (password); never re-inlined here.
//
// `pointer-coarse:text-base` — the iOS input-zoom guard, on every field-frame
// control (this file is the canonical note). iOS Safari auto-zooms the page
// when a focused editable's font-size computes under 16px, and never zooms
// back out on blur — so touch devices floor field text at text-base (16px)
// while desktop keeps the text-sm body scale. Trigger-style fields (PickerField,
// DatePicker segments) carry the same floor so a form's fields sit on one
// size on the phone.
const baseInput =
  'w-full px-3 py-2 text-sm pointer-coarse:text-base rounded-md outline-none transition-colors ' +
  'bg-[var(--color-bg-page)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-disabled)] ' +
  'disabled:bg-[var(--color-bg-surface-alt)] disabled:text-[var(--color-text-disabled)] disabled:cursor-not-allowed ' +
  'read-only:bg-[var(--color-bg-surface-alt)]';

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { type = 'text', error, autoComplete = 'off', id: idProp, className = '', selectOnFocus = true, onFocus, onBlur, onMouseUp, ...props },
  ref,
) {
  const reactId = useId();
  const inputId = idProp ?? reactId;
  const errorId = `${inputId}-error`;
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation('common');
  // onBlur routes THROUGH the hook (never past it) so select-on-focus can tell a
  // fresh entry from a re-focus — see useSelectOnFocus.
  const focusHandlers = useSelectOnFocus(selectOnFocus, { onFocus, onBlur, onMouseUp });

  // Suppress password-manager prompts on app-data fields (autoComplete 'off');
  // credential fields pass a real autoComplete, so they keep PM support.
  const pmIgnore: Record<string, string | boolean> =
    autoComplete === 'off' ? { 'data-1p-ignore': true, 'data-lpignore': 'true' } : {};

  if (type === 'password') {
    const visible = showPassword;
    return (
      <div className="space-y-1">
        <div className="relative flex items-stretch rounded-md field-frame transition-colors">
          <input
            ref={ref}
            id={inputId}
            type={visible ? 'text' : 'password'}
            autoComplete={autoComplete}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            className={`${baseInput} border-0 pe-10 ${className}`}
            {...pmIgnore}
            {...props}
            {...focusHandlers}
          />
          <button
            type="button"
            aria-label={visible ? t('actions.hidePassword') : t('actions.showPassword')}
            onClick={() => setShowPassword((v) => !v)}
            className="absolute end-1 top-1/2 -translate-y-1/2 p-1.5 min-w-[1.75rem] min-h-[1.75rem] inline-flex items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] outline-none focus-ring-inset"
          >
            {visible ? <icons.hide aria-hidden size={16} /> : <icons.reveal aria-hidden size={16} />}
          </button>
        </div>
        {error && (
          <p id={errorId} className="text-xs text-[var(--color-danger)]">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <input
        ref={ref}
        id={inputId}
        type={type}
        autoComplete={autoComplete}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={`${baseInput} field-frame ${className}`}
        {...pmIgnore}
        {...props}
        {...focusHandlers}
      />
      {error && (
        <p id={errorId} className="text-xs text-[var(--color-danger)]">
          {error}
        </p>
      )}
    </div>
  );
});
